import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Circle, Upload, UserCog, Building2, Loader2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

const ACTION_META = {
  documents: { to: "/app/worker/documents", icon: Upload, ctaKey: "checklist.goUpload" },
  profile: { to: "/app/worker/profile", icon: UserCog, ctaKey: "checklist.goProfile" },
  employer: { to: null, icon: Building2, ctaKey: "checklist.contactEmployer" },
};

export default function WorkerChecklistDialog({ open, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/worker/profile-checklist").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [open]);

  const missing = (data?.items || []).filter((i) => !i.done);
  const done = (data?.items || []).filter((i) => i.done);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="worker-checklist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-navy">{t("checklist.title")}</DialogTitle>
          <DialogDescription>{t("checklist.subtitle")}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-400" />
        ) : missing.length === 0 ? (
          <div className="my-6 flex flex-col items-center gap-2 text-center" data-testid="worker-checklist-complete">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="font-heading text-lg font-bold text-navy">{t("checklist.allComplete")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {missing.map((item) => {
              const meta = ACTION_META[item.action] || ACTION_META.employer;
              const Icon = meta.icon;
              return (
                <div key={item.key} className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid={`checklist-item-${item.key}`}>
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Circle className="h-4 w-4 shrink-0 text-amber-500" />
                    <span className="truncate text-sm font-medium text-navy">{t(`checklist.${item.key}`)}</span>
                  </div>
                  {meta.to ? (
                    <Link to={meta.to} onClick={onClose} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-cyan px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-dark" data-testid={`checklist-cta-${item.key}`}>
                      <Icon className="h-3.5 w-3.5" /> {t(meta.ctaKey)} <ArrowRight className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500" data-testid={`checklist-cta-${item.key}`}>
                      <Icon className="h-3.5 w-3.5" /> {t(meta.ctaKey)}
                    </span>
                  )}
                </div>
              );
            })}
            {done.length > 0 && (
              <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
                {done.map((item) => (
                  <div key={item.key} className="flex items-center gap-2.5 px-1 text-sm text-slate-400" data-testid={`checklist-done-${item.key}`}>
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    <span className="truncate line-through">{t(`checklist.${item.key}`)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
